import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Ghost, Clock } from "lucide-react";

const POLL_MS = 60_000;

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const SEV_TONE = {
  critical: { bg: "var(--brand-secondary)", text: "#FCA5A5", label: "🚨 3-Day Alert" },
  warning:  { bg: "#F59E0B", text: "#FCD34D", label: "2-day gap" },
  advisory: { bg: "#22D3EE", text: "#67E8F9", label: "Check-in due" },
};

export default function PlayerNotificationBell() {
  const [data, setData] = useState({ events: [], reminder: null, unseen_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/players/me/notifications"); setData(data); }
    catch (err) { console.warn("[player-notifications] load failed:", err); }
  };
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const total = data.unseen_count;
  const tone = (data.reminder?.severity && SEV_TONE[data.reminder.severity]) || SEV_TONE.advisory;

  return (
    <div ref={ref} className="relative" data-testid="player-notification-bell">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/85 hover:text-white hover:bg-white/5 transition"
        title="My notifications"
        data-testid="player-notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ backgroundColor: tone.bg }} data-testid="player-notification-bell-badge">
            {total > 9 ? "9+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-2xl ring-1 ring-white/10 z-50 overflow-hidden" style={{ backgroundColor: "var(--ink-2)" }} data-testid="player-notification-dropdown">
          <div className="px-4 py-3 border-b border-[var(--ink-border)]" style={{ backgroundColor: "var(--ink-1)" }}>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/70">My notifications</div>
            <div className="text-[10px] text-white/40 mt-0.5 font-mono-num">Last check-in: {timeAgo(data.last_log_at)}</div>
          </div>

          {data.reminder && (
            <button
              onClick={() => { setOpen(false); nav("/portal"); }}
              className="block w-full text-left px-4 py-3 border-b border-[var(--ink-border)] hover:bg-white/[0.04] transition"
              style={{ backgroundColor: data.reminder.severity === "critical" ? "rgba(217,4,41,0.12)" : "transparent" }}
              data-testid="player-reminder"
            >
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone.bg }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-white text-sm">{data.reminder.title}</div>
                  <div className="text-xs text-white/60 mt-1 leading-relaxed">{data.reminder.body}</div>
                </div>
              </div>
            </button>
          )}

          <div className="max-h-72 overflow-y-auto">
            {data.events.length === 0 && !data.reminder && (
              <div className="px-4 py-10 text-center text-sm text-white/40">All caught up. Keep grinding.</div>
            )}
            {data.events.map((e) => {
              const ghost = e.event_type === "ghost_pitcher";
              const sev = e.severity || "critical";
              const accent = ghost ? (SEV_TONE[sev]?.bg || SEV_TONE.advisory.bg) : "var(--brand-secondary)";
              const Icon = ghost ? Ghost : AlertTriangle;
              return (
                <div key={e.event_id} className="px-4 py-3 border-b border-[var(--ink-border)]" data-testid={`player-event-${e.event_id}`}>
                  <div className="flex items-start gap-3">
                    <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-display font-bold text-white text-sm truncate">
                          {ghost ? (sev === "critical" ? "🚨 3-Day Alert from staff" : "Missed check-in") : "Coach flagged you CRITICAL"}
                        </div>
                        <div className="text-[10px] text-white/40 shrink-0 font-mono-num">{timeAgo(e.created_at)}</div>
                      </div>
                      {e.notes && <div className="text-xs text-white/60 mt-1 leading-relaxed line-clamp-2">{e.notes}</div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
